/**
 * ts and vue file cannot have the same name, such as 
 * ecoplant.ts and ecoplant.vue cannot co-exist
 */
import { Component } from "vue-property-decorator";
import AppBase from "./app.base";
import { IMapLayerSource, IMapLayer, IMapViewInfo, IMapViewExtent, IMapAction, 
  IFeatureInfo, IGeoJSON, 
  IInfoSchema, IInfoProperty, ICodeList, Extent
 } from "@/components/map/types";
import { ITreeList } from "@/components/shared/types";

import { sortObjectArray } from "@/common/utils";
import { APP_URL } from "@/common/http";

import { MONTHS, BASEMAP_LAYERS, LEGEND_WATER_USE, EEcoPlantType,
         makeCropLayer, makeWateruseLayer } from "./data/ecoplant";

type EDrawerHelperPage = "" | "help" | "upload";
type EDownloadType = "shape" | "pdf" | "report" | "rid" | "flood";
type ETocOptions = null | "SEARCH" | "LAYERS" | "MAP_INFO" | "DOWNLOAD" | 
                   "RICE" | "MAIZE" | "SUGARCANE" | "CASSAVA";
const DOWNLOAD_SETS = ['shape','pdf','report','rid','flood'];
@Component
export default class EcoPlantBase extends AppBase {
  TocWidth: number = 275;
  DrawerHeight: number = 0;
  toc: ETocOptions = 'LAYERS';
  topic: ETocOptions = 'CASSAVA';
  timeNow: string = null;
  clockInterval: NodeJS.Timer = null;

  showDrawerLeft: boolean = true;
  showDrawerLeftLast: boolean = true;
  showDrawerRight: boolean = false;
  showDrawerRight2: boolean = false;
  showDrawerMiniToolbar: boolean = true;
  DrawerHelperPage: EDrawerHelperPage = 'help'; // important since we use .math() for v-if
  DrawerHelperTitle: string = null;
  DrawerHelperSubTitle: string | string[] = null;
  DrawerHelperFile: string = '/static/docs/GISagro-1.0.pdf';
  DrawerHelperAvatar: string = null;
  DrawerHelperIcon: string = null;
  enableEditUserFields: boolean = false;
  enableEditOrgFields: boolean = false;
  
  mapExtent:Extent = [95.854, 5.466, 106.064, 20.533];

  mapLayerStore: IMapLayer[] = null;
  // mapLayers: IMapLayer[] = null;  //layers to be shown on map and in legend wndow
  // auto order map layer z-index with Point on top, Line in the middle and Polygon at the bottom
  mapLayersPoint: IMapLayer[] = [];
  mapLayersLine: IMapLayer[] = [];
  mapLayersPolygon: IMapLayer[] = [];
  
  listDownloadCassava:ITreeList[] = null;
  listDownloadMaize:ITreeList[] = null;
  listDownloadSugarcane:ITreeList[] = null;
  listDownloadRice:ITreeList[] = null;
  listDownloads: ITreeList[] = [];
  
  srhRidAreas: ICodeList[] = [];
  srhRidProjects: ICodeList[] = [];
  srhSelectedRidArea = null;
  srhSelectedRidProject = null;
  srhSelectedDownloadSet: number = 0;

  // events
  onResize() {
    this.WindowWidth = window.innerWidth;
    this.DrawerHeight = window.innerHeight - 76;
    if (this.isSmallOrMobile) {
      this.TocWidth = 576;
    } else {
      this.TocWidth = 275;
    }
  };
  get MapViewPaddingLeft() : number {
    return this.showDrawerLeft ? this.TocWidth : 0;
  };

  get MapViewPaddingTop() : number {
    return 88;
  };

  get MapViewPaddingRight() : number {
    return this.showDrawerHelper ? 760 : 0;
  };

  get MapViewPaddingBottom() : number {
    return 0;
  };

  get MapViewPadding() : Extent {
    return [
      this.MapViewPaddingTop,
      this.MapViewPaddingRight,
      this.MapViewPaddingBottom,
      this.MapViewPaddingLeft
    ];
  }

  get ViewPadding() : {[key:string] : string} {
    return {
      'padding-left' : this.MapViewPaddingLeft + 'px!important',
      'padding-top' : '64px!important', // toolbar height
      'padding-right' : this.MapViewPaddingRight + 'px!important',
      'padding-bottom' : this.MapViewPaddingBottom + 'px!important'
    }
  };

  get showDrawerHelper() : boolean {
    return !this.showDrawerLeft && this.showDrawerRight2;
  }
  set showDrawerHelper(value:boolean) {
    // do nothing to avoid xxx has not setter
  }

  DateFormat() {
    return this.language=='th' ? 'dd-mm-yyyy' : 'yyyy-mm-dd';
  };

  setMapAction(action: IMapAction) {
    if (!action || !action.name || action.name==this.activeAction) {
      this.activeAction = null;
      this.showMeasurer = false;
      this.$map.setMapAction(null);
    } else if (action.name=='changeBasemap') {
      this.activeBaseMap = action.params.id;
    } else if (action.name=='printMap') {
      this.showMapForPrint();
    } else if (action.name=='undo') {
    } else if (action.name=='redo') {
    } else if (action.name=='save') {
    } else if (action.name=='uploadGpx') {
    } else if (action.name.match(/zoom|pan|zoomToExtent/)) {
      // these actions will not be remained
      this.$map.changeMapView(action.name, action.params);
    } else if (action.name=='showLocator') {
      this.showLocator = true;
      if (!this.showLocatorAt) {
        this.showLocatorAt = 'top-center';
      }
    } else {
      // these should be mappint tools.
      // their actions will remain till it is changed
      if (action.name==this.activeAction) {
        action.name = null; // toggle off
      }
      this.activeAction = action.name;
      if (action.name == 'measure') {
        action.params.target = this.$refs.measureResult;
        this.showMeasurer = !!this.activeAction;
        if (!this.showMeasurerAt) {
          this.showMeasurerAt = 'bottom-left';
        }
        setTimeout(() => {
          this.$map.setMapAction(action.name, action.params || {});
        }, 100); // give at least 50ms for UI to be ready
      } else {
        this.showMeasurer = false;
        this.$map.setMapAction(action.name, action.params || {});
      }
    }
  };
  
  get cropPlant() : string {
    return ('crop-' + this.topic.toLowerCase().replace('_','-')).toCamelCase();
  }
  
  async getMapLayerStore() {
    try {
      let crop:EEcoPlantType = <EEcoPlantType> this.topic.toLowerCase();
      let ds = await this.User.requestResource('mis/dataset_' + crop);
      this.mapLayerStore = [{
        header: 'growingAreaEvery2Weeks',
        replacer: ['crop' + crop.capitalize()],
        children: [...ds.map((data,index) => {
          return makeCropLayer(crop, data, (index+1), index==0);
        }),makeWateruseLayer(crop)]
      }, /*makeWateruseLayer(crop),*/ ...BASEMAP_LAYERS];
      this.mapLayerStore.forEach((layer) => {
        //layer.parent = {
        //  title: 'root', // any name, not displayed
        //  children: this.mapLayerStore
        //};
        layer.parent = null;
        this.showMapLayer(layer);
      }, this);
    } catch (error) {
      console.log('ecoplant.base:' + error);
    }
  };
  
  // for ecoplant, rice does not use this
  async changeCropLayer() {
    let crop:EEcoPlantType = <EEcoPlantType> this.topic.toLowerCase();
    let ds = await this.User.requestResource('mis/dataset_' + crop);
    let cropLayers:IMapLayer[] = ds.map((data,index) => {
      return makeCropLayer(crop, data, (index+1), index==0);
    });
    this.mapLayerStore[0].replacer = [
      'crop' + crop.capitalize()
    ];
    let theNativeLayer;
    let activeLayer;
    let ms;
    this.mapLayerStore[0].children.forEach((child,index) => {
      if (child.type=='radio') {
        let nativeLayer = child.mapSources[0].nativeLayer;
        if (nativeLayer) {
          theNativeLayer = nativeLayer;
        }
        // IMPORTANT, get nativeLayer first before changing the mapSources
        child.title = cropLayers[index].title;
        child.mapSources = cropLayers[index].mapSources;
        child.mapSources[0].nativeLayer = nativeLayer; // restore nativeLayer
        child.legend = cropLayers[index].legend; // these items are legends
        if (child.active || child.checked) {
          activeLayer = child;
        }
      }
    });
    if (!activeLayer) {
      activeLayer = this.mapLayerStore[0].children[0];
      activeLayer.active = true;
      activeLayer.checked = true;
      activeLayer.visible = true;
      activeLayer.mapSources[0].visible = true;
    }
    if (theNativeLayer) {
      ms = theNativeLayer.getSource();
      ms.updateParams(activeLayer.mapSources[0].params);
      theNativeLayer.setVisible(true);
      ms.tileCache.expireCache({});
      ms.tileCache.clear();
      ms.refresh();
    }

    //water use layer
    let last = this.mapLayerStore[0].children.length - 1;
    let wateruse = this.mapLayerStore[0].children[last];
    wateruse.legend = crop=='rice' ? LEGEND_WATER_USE : [{
      title: ['(','noData',')']
    }];
    if (crop!='rice') {
      wateruse.checked = false;
    }
    wateruse.mapSources[0].params.LAYERS = 'ecoplant:wateruse_' + crop + '_present';
    theNativeLayer = wateruse.mapSources[0].nativeLayer;
    if (!!theNativeLayer) {
      ms = theNativeLayer.getSource();
      ms.updateParams(
        wateruse.mapSources[0].params
      );
      theNativeLayer.setVisible(wateruse.checked);
      ms.tileCache.expireCache({});
      ms.tileCache.clear();
      ms.refresh();
    }

    this.setVisibleLayers();
  };

  showMapForPrint() {
    let url = window.location.protocol + '//' + 
              window.location.hostname + 
              (window.location.port ? ':' + window.location.port : '') +
              '/printecoplant';
    let ext:IMapViewExtent = this.$map.getViewExtent();
    let vinfo:IMapViewInfo = this.$map.getViewInfo();
    let mapFilter = {
      ProvinceCode: this.mapFilter.ProvinceCode,
      AmphoeCode: this.mapFilter.AmphoeCode,
      TambonCode: this.mapFilter.TambonCode
    }
    this.setVisibleLayers();
    let mapLayers = this.VisibleLayers.map((vl) => {
      return this.cloneMapLayers(vl,false);
    });
    this.$store.commit('notifyChange', {
      Storage: {
        language: this.language,
        uid: this.User.HalloId,
        userOrgc: this.User.Organization ? this.User.Organization.Code : null,
        token: this.User.Token,
        mapExtent: ext.wgs84,
        mapCenter: vinfo.paddingCenter.wgs84,
        mapZoom: vinfo.zoom,
        basemap: this.activeBaseMap,
        mapLayers: mapLayers,
        mapFilter: mapFilter,
        plant: this.topic
      }
    });
    let win = window.open(url, 'gisagroprint');
    win.focus();
  }

  showMapLayer(layer:IMapLayer) {
    if (layer.mapSources && layer.mapSources.length>0) {
      layer.mapSources.forEach((ms) => {
        if (layer.type=='checkbox' || 
           (layer.type=='radio' && layer.checked)) { 
          // create only one nativeLayer for radio groups
          // but all checkbox nodes should have their own nativeLayer
          this.addLayerToMap(layer);
        }
      });
    }
    // defined actions event listener
    // when layer.type is defined and when click the check icon,
    // the action is 'changeChecked'
    if (layer.type || layer.actions || layer.mapSources) {
      layer.doAction = this.doMapLayerAction;
    }
    if (layer.children) {
      layer.children.forEach((child) => {
        child.parent = layer;
        this.showMapLayer(child);
      }, this);
    }
  };

  addLayerToMap(layer: IMapLayer) {
    if (layer.geomType=='Point') {
      this.mapLayersPoint.unshift(layer);
    } else if (layer.geomType=='Line') {
      this.mapLayersLine.unshift(layer);
    } else {
      this.mapLayersPolygon.unshift(layer);
    }
  };

  doMapLayerAction(data) {
    // data: {item:IMapLayer,action:{}}
    let item:IMapLayer = data.item;
    let action: any = data.action;
    if (action.action=='changeChecked') {
      if (item.type=='checkbox') {
        // every node of checkbox should have a native map layer associated
        if (item.mapSources) {
          this.setLayervisibility(item.mapSources, item.checked);
        }
      } else if (item.type=='radio') {
        // all nodes of the same radio group share a common map layer
        let cropLayer;
        this.mapLayerStore[0].children.forEach((child) => {
          if (child.type=='radio') {
            if (child.mapSources[0].nativeLayer) {
              cropLayer = child.mapSources[0].nativeLayer;
            }
            if (child != item) {
              child.checked = false;
              child.visible = false;
              child.mapSources[0].visible = false;
            }
          }
        });
        item.mapSources[0].visible = item.visible;
        if (cropLayer) {
          setTimeout(() => {
            cropLayer.getSource().updateParams(item.mapSources[0].params2);
            cropLayer.setVisible(item.visible);
          },100);
        }
      }
      this.setVisibleLayers();
    }
  };
  
  setLayervisibility(mapSources:IMapLayerSource[], visibility) {
    mapSources.forEach((ms) => {
      if (ms.nativeLayer) {
        ms.nativeLayer.setVisible(visibility);
      }
    });
  };

  selectDownload(data) {
    let item:ITreeList = data.item;
    let action: any = data.action;
    if (item.parent.children && item.parent.children.length>0) {
      item.parent.children.forEach((child) => {
        if (child!=item) {
          child.checked = false;
        }
      });
    }
  };

  get selectedDownload() : ITreeList {
    let download:ITreeList = null;
    if (this.listDownloads && this.listDownloads.length>0) {
      let list = this.listDownloads[this.srhSelectedDownloadSet];
      let start = (new Date()).getTime();
      let end = (new Date()).getTime();
      while (!(list.children && list.children.length>0) && (end - start) <= 1000) {
        // waiting for max 1 second to fetch download list
        end = (new Date()).getTime();
      }
      if (list.children && list.children.length>0) {
        list.children.forEach((d) => {
          if (d.checked) {
            download = d;
            return;
          }
        });
      }
    }
    return download;
  };

  get selectedDownloadUrl() : string {
    return !this.selectedDownload ? '#' : 
           APP_URL + '/' + this.selectedDownload.url + '/' + this.selectedDownload.uri;
  }
  
  async doDownload(download:ITreeList) {
    try {
      const topic:EEcoPlantType = <EEcoPlantType> this.topic.toLowerCase();
      const rsc = 'file/download/' + topic + '_' + DOWNLOAD_SETS[this.srhSelectedDownloadSet];
      const data = await this.User.requestResource(rsc + '/' + download.uri);
      const url = window.URL.createObjectURL(new Blob([data]));
      window.open(url);
      //const url = download.url + "/" + download.id;
      //const downloadLink = <HTMLElement> this.$refs.downloadLink;
      //downloadLink['href'] = url;
      //downloadLink.click();
      //URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error);
    }
  };

  yyyymmddToDateString(yyyymmdd:string) : string {
    yyyymmdd += '';
    let yyyy = parseInt(yyyymmdd.substr(0,4));
    let mm = parseInt(yyyymmdd.substr(4,2)) - 1;
    let dd = parseInt(yyyymmdd.substr(6,2));
    let title = (dd<=15) ? '01 - 15' : '16 - ' + dd;
    title += ' ' + MONTHS[mm] + ' ' + (yyyy<=2500 ? (yyyy + 543) : yyyy);
    return title;
  };

  yyyymmddToDate(yyyymmdd:string) : Date {
    yyyymmdd += '';
    let yyyy = parseInt(yyyymmdd.substr(0,4));
    let mm = parseInt(yyyymmdd.substr(4,2)) - 1;
    let dd = parseInt(yyyymmdd.substr(6,2));
    return new Date(yyyy, mm, dd);
  };

  YYYYMMDD: string;
  getDownloadListYYYYMMDD(name:string,crop:EEcoPlantType,type:EDownloadType) {
    let topic:EEcoPlantType = <EEcoPlantType> this.topic.toLowerCase();
    let yyyymmdd;
    try {
      switch (type) {
      case 'shape':
        yyyymmdd = name.substring(0,8);
        this.YYYYMMDD = this.YYYYMMDD || yyyymmdd;
        break;
      case 'pdf':
      case 'report':
        yyyymmdd = (parseInt(name.split('_')[2]) - 543) + '';
        yyyymmdd = yyyymmdd.substr(4) + yyyymmdd.substr(2,2) + yyyymmdd.substr(0,2);
        this.YYYYMMDD = this.YYYYMMDD || yyyymmdd;
        break;
      case 'rid':
        yyyymmdd = name.split('_')[2];
        this.YYYYMMDD = this.YYYYMMDD || yyyymmdd;
        break;
      case 'flood':
        yyyymmdd = this.YYYYMMDD;
        break;
      }
    } catch (error) {
      yyyymmdd = '';
    }
    return yyyymmdd;
  };

  getDownloadListCode(name:string, type:EDownloadType) : string {
    let topic:EEcoPlantType = <EEcoPlantType> this.topic.toLowerCase();
    let code;
    try {
      name += '';
      switch (type) {
      case 'shape':
        code = name.substring(0,8);
        break;
      case 'pdf':
      case 'report':
        code = name.split('_')[0] + '';
        break;
      case 'rid':
        code = name.split('_')[1] + '';
        break;
      case 'flood':
        code = ('000' + parseInt(name.substr(name.indexOf(' ') + 1))).slice(-2);
        break;
      }
    } catch (error) {
      code = '';
    }
    return code;
  };

  getDownloadListTitle(code:string, crop:EEcoPlantType, type:EDownloadType) : string {
    let title;
    try {
      code += '';
      switch (type) {
        case 'shape':
          title = this.yyyymmddToDateString(code);
          break;
        case 'pdf':
        case 'report':
          title = this.getProvinceNameByCode(code);
          break;
        case 'rid':
          title = (code=='00' || code=='0') ? 'ภาพรวม 17 สำนัก' : 'สำนักชลประทาน ' + code;
          break;
        case 'flood':
          title = 'ภัยพิบัติข้าวฉบับ ' + code;
          break;
        default:
          title = code; //
        }
    } catch (error) {
      title = '';
    }
    return title;
  };

  async getDownloadList(parent:ITreeList, crop:EEcoPlantType, type:EDownloadType) {
    try {
      let files = await this.User.requestResource('file/list/' + crop + '_' + type);
      parent.children = files.map((file) => {
        let code = this.getDownloadListCode(file.file, type);
        let list:ITreeList = {
          parent: parent,
          uri: file.file,
          url: file.path,
          code: code,
          title: this.getDownloadListTitle(code, crop, type),
          type: 'radio',
          checked: false,
          doAction: this.selectDownload
        };
        return list;
      });
      let date;
      if (type=='shape') {
        sortObjectArray(parent.children, 'code desc');
      } else if (type=='flood') {
        sortObjectArray(parent.children, 'title desc');
      } else if (type=='rid') {
        sortObjectArray(parent.children, 'code');
      } else {
        sortObjectArray(parent.children, 'title');
        parent.children[0].title = 'ภาพรวม 77 จังหวัด';
        if (type=='report' && crop=='rice') {
          parent.children[1].title = 'ภาพรวมรายภาค';
        }
      }
      let yyyymmdd = this.getDownloadListYYYYMMDD(parent.children[0].uri,crop,type);
      if (type=='flood') {
        parent.children.unshift({
          header: null
        });
      } else {
        parent.children.unshift({
          header: 'latestUpdate',
          replacer: [this.yyyymmddToDate(yyyymmdd)]
        });
      }
      return parent.children;
    } catch (error) {
      return null;
    }
  }

  async getDownloadLists() {
    let topic:EEcoPlantType = <EEcoPlantType> this.topic.toLowerCase();
    let what = 'listDownload' + topic.capitalize();
    if (!this[what]) {
      let ds;
      let lists:ITreeList[] = [{
        title: 'downloadShapeFile'
      },{
        title: 'downloadMapFile'
      },{
        title: 'downloadReportFile'
      }];
      lists[0].children = await this.getDownloadList(lists[0], topic, 'shape');
      lists[1].children = await this.getDownloadList(lists[1], topic, 'pdf');
      lists[2].children = await this.getDownloadList(lists[2], topic, 'report');
      if (topic=='rice') {
        lists.push({
          title: 'downloadRIDFile'
        });
        lists.push({
          title: 'downloadPlantDisaster'
        });
        lists[3].children = await this.getDownloadList(lists[3], topic, 'rid');
        lists[4].children = await this.getDownloadList(lists[4], topic, 'flood');
      }
      this.listDownloads = lists;
      this[what] = this.listDownloads;
    } else {
      this.listDownloads = this[what];
    }
  };

  async fetchRidAreas() {
    try {
      let data = await this.User.requestResource('codelist/rid');
      this.srhRidAreas = data.map((d) => {
        return d;
      });
    } catch (error) {
    }
  };

  async fetchRidProjects() {
    try {
      if (!this.srhSelectedRidArea) {
        this.srhSelectedRidProject = null;
        this.srhRidProjects = [];
      } else {
        let rest = 'codelist/rid_project/' + this.srhSelectedRidArea;
        let data = await this.User.requestResource(rest);
        this.srhRidProjects = data.map((d) => {
          return d;
        });
      }
    } catch (error) {
    }
  };

  async zoomToRidArea() {
    try {
      if (!this.srhSelectedRidArea) {
        this.$map.clearAoi();
        this.$map.MapExtent = this.mapExtent;
      } else {
        let rid:ICodeList;
        this.srhRidAreas.forEach((r) => {
          if (r.code==this.srhSelectedRidArea) {
            rid = r;
            return;
          }
        });
        if (!rid.feature) {
          let rsc = 'gis/rid/' + this.srhSelectedRidArea + '?crs=3857';
          rid.feature = await this.User.requestResource(rsc);
          rid.feature = this.$map.GeoJSONToFeatures(rid.feature);
        }
        this.$map.addFeaturesToLayer(rid.feature, "AoiLayer");
      }
    } catch (error) {
    }
  };

  async zoomToRidProject() {
    try {
      if (!this.srhSelectedRidProject) {
        this.zoomToRidArea();
      } else {
        let rid:ICodeList;
        this.srhRidProjects.forEach((r) => {
          if (r.code==this.srhSelectedRidProject) {
            rid = r;
            return;
          }
        });
        if (!rid.feature) {
          let rsc = 'gis/rid_project/' + this.srhSelectedRidProject + '?crs=3857';
          rid.feature = await this.User.requestResource(rsc);
          rid.feature = this.$map.GeoJSONToFeatures(rid.feature);
        }
        this.$map.addFeaturesToLayer(rid.feature, "AoiLayer");
      }
    } catch (error) {
    }
  };
  
  clearSelectedAoI(aoiVariableName:string) {
    if (aoiVariableName) {
      if (aoiVariableName.match(/ProvinceCode|AmphoeCode|TambonCode/)) {
        this.mapFilter[aoiVariableName] = null;
      } else {
        this[aoiVariableName] = null;
      }
    } else {
      this.mapFilter.ProvinceCode = null;
      this.mapFilter.AmphoeCode = null;
      this.mapFilter.TambonCode = null;
      this.srhSelectedRidArea = null;
      this.srhSelectedRidProject = null;
    }
  };

  onProvinceChange() {
    this.zoomToProvince();
    this.fetchAmphoes();
  };

  onAmphoeChange() {
    this.zoomToAmphoe();
    this.fetchTambons();
  };
  
  onTambonChange() {
    this.zoomToTambon();
  };

  onRidAreaChange() {
    this.zoomToRidArea();
    this.fetchRidProjects();
  };

  onRidProjectChange() {
    this.zoomToRidProject();
  };
  
  buildMapInfo(data:IGeoJSON, editible?:boolean) : IInfoSchema {
    editible = (editible===true);
    let info:IInfoSchema;
    let title = data.featureClass + '';
    let p;
    let props: IInfoProperty[] = [];
    for (p in data.properties) {
      props.push({
        name: p,
        alias: p,
        value: data.properties[p]
      });
    }
    info = {
      title: title.capitalize(),
      items: [{
        properties: props
      }]
    }
    info.canEdit = editible;
    if (this.showMapInfoTab>=info.items.length) {
      this.showMapInfoTab = 0;
    }
    return info;
  }

  async showFeatureInfo(info:IFeatureInfo) {
    this.featureInfo = info;
    try {
      this.featureInfo.fetching = true;
      let f = info.feature;
      let ft, url = 'gis/';
      let schema:IInfoSchema;
      let editible:boolean = false;
      let d = this.$map.getMetersPerPixel() * 5;
      url += 'tambon/' + info.location.wgs84[0] + ',' + info.location.wgs84[1] + ',' + d;
      let data:IGeoJSON = <IGeoJSON> await this.User.requestResource(url);
      if (data.features && data.type=='FeatureCollection') {
        data = data.features[0];
      }
      //this.mapInfo = this.buildMapInfo(data, editible);
      this.featureInfo.fetching = false;
      //this.toc = 'MAP_INFO';
      //this.showDrawerLeft = true;
      let infoText, p = data.properties;
      if (data.featureClass.match(/tambon/i)) {
        //infoText = 'ต.' + p.tambon + ' อ.' + p.amphoe + ' จ.' + p.province;
        infoText = p.tambon + ' ' + p.amphoe + ' ' + p.province;
      } else {
        infoText = p.proj_name || p.name_th || p.name_en || p.owner_name ||
                  (p.tambon + ' ' + p.amphoe + ' ' + p.province);
      }
      this.$map.showInfoPopup('queryMapInfo', infoText, {
        position: info.location.map,
        offset: [0,-3],
        zoomTo: false
      });
    } catch (error) {
      this.featureInfo.fetching = false;
      if (error.id=='not_found' || error.id=='no_content') {
        //this.mapInfo = {
        //  title: 'noData',
        //  items: []
        //}
      }
      this.$map.hideInfoPopup();
    }
  };
}